import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SERVICES } from "../data/services";
import { SITE } from "../data/site";
import styles from "./Topbar.module.css";

type Service = (typeof SERVICES)[number];

function scrollHomeToTop(): void {
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
}

const PRIMARY = [
  { href: "/#services", label: "Services" },
  { href: "/#about", label: "About" },
  { href: "/#work", label: "Transformations" },
  { href: "/#reviews", label: "Kind words" },
  { href: "/#visit", label: "Visit" },
  { href: "/#faq", label: "Before you book" },
];

export function Topbar() {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuItemRef = useRef<HTMLDivElement | null>(null);
  const mobileToggleRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const subMenuId = useId();

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Dismiss Services dropdown on Escape; close the dropdown when clicking
  // anywhere outside the menu item (covers both touch and mouse affordances).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        const trigger = menuItemRef.current?.querySelector<HTMLButtonElement>(
          "button[data-services-trigger]",
        );
        trigger?.focus();
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (!menuItemRef.current) return;
      if (!menuItemRef.current.contains(event.target as Node | null)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!open) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    const focusable = drawer.querySelector<HTMLElement>("a, button, summary");
    focusable?.focus();

    const background = document.querySelectorAll<HTMLElement>("#root > main, #root > footer");
    background.forEach((element) => {
      element.inert = true;
    });

    const close = () => {
      setOpen(false);
      requestAnimationFrame(() => mobileToggleRef.current?.focus());
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || !drawer) return;
      const items = [...drawer.querySelectorAll<HTMLElement>("a, button, summary")];
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      background.forEach((element) => {
        element.inert = false;
      });
    };
  }, [open]);

  const closeMobileMenu = () => {
    setOpen(false);
    mobileToggleRef.current?.focus();
  };

  return (
    <header className={`${styles.topbar} ${solid ? styles.solid : ""}`}>
      <div className={`container-wide ${styles.inner}`}>
        <Link
          to="/"
          className={styles.brand}
          aria-label={`${SITE.name} home, scroll to top`}
          onClick={scrollHomeToTop}
        >
          <span className={styles.wordmark}>{SITE.name.toLowerCase()}</span>
        </Link>

        <nav className={styles.nav} aria-label="Primary">
          <div
            ref={menuItemRef}
            className={styles.menuItem}
            onMouseEnter={() => setMenuOpen(true)}
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button
              type="button"
              data-services-trigger
              className={styles.link}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-controls={subMenuId}
              onClick={() => setMenuOpen((value) => !value)}
              onFocus={() => setMenuOpen(true)}
              onBlur={(event) => {
                if (!menuItemRef.current) return;
                const next = event.relatedTarget as Node | null;
                if (!next || !menuItemRef.current.contains(next)) {
                  setMenuOpen(false);
                }
              }}
            >
              Services
              <span className={styles.chevron} aria-hidden="true">
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path
                    d="M1 1l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <div
              id={subMenuId}
              className={`${styles.subMenu} ${menuOpen ? styles.subMenuOpen : ""}`}
              role="menu"
              aria-label="Services"
            >
              <ul>
                {SERVICES.map((service: Service) => (
                  <li key={service.slug}>
                    <Link
                      to={`/services/${service.slug}`}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {PRIMARY.slice(1).map((item) => (
            <a key={item.href} className={styles.link} href={item.href}>
              {item.label}
            </a>
          ))}
          <a className={`btn btn--primary btn--sm ${styles.cta}`} href="/#book">
            Book
          </a>
        </nav>

        <button
          type="button"
          ref={mobileToggleRef}
          className={styles.toggle}
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          onClick={() => setOpen((value) => !value)}
        >
          <span aria-hidden="true">{open ? "\u2715" : "\u2630"}</span>
        </button>
      </div>

      {open && (
        <div
          ref={drawerRef}
          className={`container-wide ${styles.drawer}`}
          id="mobile-nav"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <details className={styles.drawerGroup}>
            <summary className={styles.drawerLink}>Services</summary>
            <ul className={styles.drawerSub}>
              {SERVICES.map((service: Service) => (
                <li key={service.slug}>
                  <Link to={`/services/${service.slug}`} onClick={closeMobileMenu}>
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </details>
          {PRIMARY.slice(1).map((item) => (
            <a
              key={item.href}
              className={styles.drawerLink}
              href={item.href}
              onClick={closeMobileMenu}
            >
              {item.label}
            </a>
          ))}
          <a className={`btn btn--primary ${styles.drawerCta}`} href="/#book" onClick={closeMobileMenu}>
            Book an appointment
          </a>
        </div>
      )}
    </header>
  );
}
